import React from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from './ui/alert-dialog';
import { ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Intellectual-property notice shown once per fresh login/register.
 * The user must explicitly Accept to enter, or Decline to be logged out.
 * Backed by `pendingIpNotice` state in AuthContext.
 *
 * Every Accept click is logged server-side (best-effort) to
 * /api/legal/ip-notice/accept for legal audit purposes.
 */
export default function IpNoticeDialog() {
  const { pendingIpNotice, acknowledgeIpNotice, logout } = useAuth();
  const navigate = useNavigate();

  const handleAccept = () => {
    // Fire-and-forget — never block the user on an audit-log call.
    axios.post(`${API}/legal/ip-notice/accept`, {}).catch(() => {});
    acknowledgeIpNotice();
  };

  const handleDecline = () => {
    acknowledgeIpNotice();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <AlertDialog open={pendingIpNotice}>
      <AlertDialogContent
        className="max-w-lg"
        data-testid="ip-notice-dialog"
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <AlertDialogHeader>
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <AlertDialogTitle
              className="text-lg font-semibold"
              style={{ fontFamily: 'Manrope, sans-serif' }}
            >
              Intellectual Property Notice
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="pt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
            This portal is the intellectual property of <strong>EYB Consulting Pty Ltd</strong>.
            Replication, copying, distribution, or reproduction of this platform, its design,
            functionality, or content is strictly prohibited.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="gap-2">
          <AlertDialogCancel
            onClick={handleDecline}
            className="border-slate-300"
            data-testid="ip-notice-decline-btn"
          >
            Decline &amp; Exit
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleAccept}
            className="bg-primary hover:bg-primary/90"
            data-testid="ip-notice-accept-btn"
          >
            Accept &amp; Continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
